import { User, AppState, LoginState, isLoggedInState } from "../components/portal/state";
import * as React from "react"
import * as i18next from 'i18next'
import * as Components from '../components/components'
import { Action, loadingAsyncState, unloadedAsyncState, onlyIf, mkWidget, AsyncState, Widget, string, div } from "widgets-for-react";
import { State, ExtraUserInfo } from "./portal_client";
import { getProducts } from "./portal_utils/api"
import { ProductConfiguration, monthOptions, yearOptions, QuotationResponse, RequestToRetry } from "./portal/types";
import { Overlay } from "./overlay";
import { QuotationStatusElement } from "../components/quotation_status"
import { asyncWithConnectionError } from '../utils/widgets';
import { pushEventToDatalayer } from '../utils/analytics';
import { LoginFlowStyle } from './portal/pages/style.mod';
import { Counter } from "./counter";

export type QuotationRequestProps = {
  user: () => User<ExtraUserInfo>,
  setState: (_: Action<LoginState<State, ExtraUserInfo>>) => void,
  state: LoginState<State, ExtraUserInfo>,
}

export class QuotationRequest extends React.Component<QuotationRequestProps>  {
  constructor(props: QuotationRequestProps, context: any) {
    super(props, context)
  }

  componentDidMount() {
    let products = loadingAsyncState(() =>
      getProducts(isLoggedInState(this.props.state) ? this.props.state.appState.language_code : "").then(result => {
        return result
      }))
    this.props.setState(s => {
      if (isLoggedInState(s)) {
        return ({ ...s, appState: { ...s.appState, products } })
      }
    })
  }

  handleQuotationSubmit = (e: React.FormEvent<HTMLFormElement>, props: QuotationRequestProps) => {
    e.preventDefault();
    let currentErrors = new Array()
    if (!(props.state.appState.quotation_request.project_city && props.state.appState.quotation_request.project_country && props.state.appState.quotation_request.project_name && props.state.appState.quotation_request.required_delivery_date.month && props.state.appState.quotation_request.required_delivery_date.year)) {
      currentErrors.push(i18next.t(`quotation_request:errors.not_all_fields`))
    }

    if (props.state.appState.productConfigurations.size < 1) {
      currentErrors.push(i18next.t(`quotation_request:errors.need_product`))
    }

    if (currentErrors.length > 0) {
      props.setState(s => {
        if (isLoggedInState(s)) {
          return ({...s, appState: {...s.appState, errorMessages: currentErrors}})
        }
      })
    } else {
      document.documentElement.classList.add('alert--toggled');
      props.setState(s => {
        if (isLoggedInState(s)) {
          return ({
            ...s,
            appState: {
              ...s.appState,
              isAlertTypeOpen: {
                ...s.appState.isAlertTypeOpen,
                quotationSubmit: !s.appState.isAlertTypeOpen.quotationSubmit
              }
            }
          })
        }
      })
    }

  }

  notifications = <a, b extends {kind:string}, extra_user_info>(
    s0:LoginState<State, extra_user_info>,
    options? : { style?: LoginFlowStyle; key?: string }
  ): Widget<Action<AppState<a, b, extra_user_info>>> => {
    return onlyIf(s0.appState.errorMessages.length > 0,
      div<Action<AppState<a, b, extra_user_info>>>()([
        div<Action<AppState<a, b, extra_user_info>>>({
          key: "quotation-request-alert",
          role: "alert",
          className: "form__description form__error"
        })(
          s0.appState.errorMessages.map(error => string({readonly_element: "p", readonly: true})(error).never())   
        )
      ]))
  }

  render() {
    let state = this.props.state
    return isLoggedInState(state) ? state.appState.quotation_response.kind=="unloaded" ?
      <>
        <h1 className="sr-text">Products overview</h1>
        <form action="post" className="form--quotation-request" onSubmit={e => this.handleQuotationSubmit(e, this.props)}>

          {/* ----- Project information form */}
          <section className="section section--project-info container container--m gutter--m">
            <div className="section__inner">
              <header className="section__header">
                <h2 className="section__title title--l">
                  {i18next.t(`quotation_request:project_information.title.a`)}
                  <span> {i18next.t(`quotation_request:project_information.title.b`)}</span>
                </h2>
              </header>

              <div className="row">
                <div className="col col--60">
                  {/* Project name */}
                  <div className="form__group">
                    <label htmlFor="quotation-request-name" className="form__label">{i18next.t(`quotation_request:project_information.fields.name`)}</label>
                    {Components.String(
                      true,
                      'edit',
                      () => isLoggedInState(state) ? state.appState.quotation_request.project_name : "",
                      v => this.props.setState(s => {
                        if (isLoggedInState(s)) {
                          return ({
                            ...s, appState: { ...s.appState, quotation_request: { ...s.appState.quotation_request, project_name: v } }
                          })
                        }
                      }),
                      'quotation-request-name',
                      'form__input'
                    )}
                  </div>
                </div>

                <div className="col col--40">
                  <div className="row row--nowrap">
                    {/* Month of installation */}
                    <div className="form__group col">
                      <label htmlFor="quotation-request-required-delivery-month" className="form__label">{i18next.t(`quotation_request:project_information.fields.installation_month`)}</label>
                      {Components.Union(
                        true,
                        'edit',
                        monthOptions,
                        () => isLoggedInState(state) ? state.appState.quotation_request.required_delivery_date.month : "0",
                        v => this.props.setState(s => {
                          if (isLoggedInState(s)) {
                            let required_delivery_date = s.appState.quotation_request.required_delivery_date
                            required_delivery_date.month = v
                            return ({ ...s, appState: { ...s.appState, quotation_request: { ...s.appState.quotation_request, required_delivery_date: required_delivery_date } } })
                          }
                        }),
                        'quotation-request-required-delivery-month',
                        `${i18next.t('quotation_request:project_information.fields.installation_month_placeholder')}`
                      )}
                    </div>

                    {/* Year of installation */}
                    <div className="form__group col">
                      <label htmlFor="quotation-request-required-delivery-year" className="form__label">{i18next.t(`quotation_request:project_information.fields.installation_year`)}</label>
                      {Components.Union(
                        true,
                        'edit',
                        yearOptions.map(option => ({ value: option, label: option })).toList(),
                        () => isLoggedInState(state) ? state.appState.quotation_request.required_delivery_date.year : "0",
                        v => this.props.setState(s => {
                          if (isLoggedInState(s)) {
                            let required_delivery_date = s.appState.quotation_request.required_delivery_date
                            required_delivery_date.year = v
                            return ({ ...s, appState: { ...s.appState, quotation_request: { ...s.appState.quotation_request, required_delivery_date: required_delivery_date } } })
                          }
                        }),
                        'quotation-request-required-delivery-year',
                        `${i18next.t('quotation_request:project_information.fields.installation_year_placeholder')}`
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <div className="row">
                <div className="col col--60">
                  {/* Project address */}
                  <div className="form__group">
                    <label htmlFor="quotation-request-project-address" className="form__label">{i18next.t(`quotation_request:project_information.fields.address`)}</label>
                    {Components.String(
                      true,
                      'edit',
                      () => isLoggedInState(state) ? state.appState.quotation_request.project_address : "",
                      v => this.props.setState(s => {
                        if (isLoggedInState(s)) {
                          return ({
                            ...s, appState: { ...s.appState, quotation_request: { ...s.appState.quotation_request, project_address: v } }
                          })
                        }
                      }),
                      'quotation-request-project-address',
                      'form__input'
                    )}
                  </div>
                </div>

                <div className="col col--40">
                  <div className="row row--nowrap">
                    {/* City */}
                    <div className="form__group col">
                      <label htmlFor="quotation-request-project-city" className="form__label">{i18next.t(`quotation_request:project_information.fields.city`)}</label>
                      {Components.String(
                        true,
                        'edit',
                        () => isLoggedInState(state) ? state.appState.quotation_request.project_city : "",
                        v => this.props.setState(s => {
                          if (isLoggedInState(s)) {
                            return ({
                              ...s, appState: { ...s.appState, quotation_request: { ...s.appState.quotation_request, project_city: v } }
                            })
                          }
                        }),
                        'quotation-request-project-city',
                        'form__input'
                      )}
                    </div>

                    {/* Country */}
                    <div className="form__group col">
                      <label htmlFor="quotation-request-project-country" className="form__label">{i18next.t(`quotation_request:project_information.fields.country`)}</label>
                      {Components.String(
                        true,
                        'edit',
                        () => isLoggedInState(state) ? state.appState.quotation_request.project_country : "",
                        v => this.props.setState(s => {
                          if (isLoggedInState(s)) {
                            return ({
                              ...s, appState: { ...s.appState, quotation_request: { ...s.appState.quotation_request, project_country: v } }
                            })
                          }
                        }),
                        'quotation-request-project-country',
                        'form__input'
                      )}
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </section>

          {/* ----- Product configurator */}
          <Overlay type="panel" {...this.props} />

          {/* ----- Product overview */}
          <section className="section section--products container container--m gutter--m">
            <div className="section__inner">
              <header className="section__header">
                <h2 className="section__title title--l">
                  {i18next.t(`quotation_request:product_overview.title.a`)}
                  <span> {i18next.t(`quotation_request:product_overview.title.b`)}</span>
                </h2>
              </header>

              <button
                type="button"
                className="section__button button button--text-icon"
                aria-controls="a11y-overlay-collapse"
                aria-expanded={state.appState.isPanelOpen}
                onClick={() => {
                  pushEventToDatalayer({ category: "Productconfigurator", action: "Add product" })
                  document.documentElement.classList.add('panel--toggled');
                  this.props.setState(s => {
                    if (isLoggedInState(s)) {
                      let prodconfigs = s.appState.productConfigurations
                      let freshProductConfigutation = Math.max(0, ...prodconfigs.toIndexedSeq().toArray().map(pc => +pc.Id)) + 1
                      let productConfiguration: ProductConfiguration = {
                        Id: freshProductConfigutation.toString(),
                        ProductId: "0",
                        Count: 0,
                        CharacteristicArray: unloadedAsyncState(),
                        CharacteristicValues: [],
                        SpecialFeature: "",
                        SelectedCharacteristic: "ProductId",
                        Submitted: false
                      }
                      prodconfigs = prodconfigs.set(productConfiguration.Id, productConfiguration)
                      return ({ ...s, appState: { ...s.appState, showProducts: true, isPanelOpen: !this.props.state.appState.isPanelOpen, selectedProductConfiguration: freshProductConfigutation.toString(), productConfigurations: prodconfigs } })
                    }
                  })
                }}>
                <span className="button__label">{i18next.t(`quotation_request:product_overview.clickables.add_product`)}</span>
                <svg className="button__icon" width="20px" height="20px" aria-hidden="true">
                  <use xlinkHref="#icon--plus" />
                </svg>
              </button>

              {isLoggedInState(state) && state.appState.productConfigurations.isEmpty()
                ? <div className="empty-state">
                    <p>{i18next.t('quotation_request:product_overview.empty_state')}</p>
                    {state.appState.inDarkMode
                      ? <svg className="empty-state__icon" width="150" height="150" aria-hidden="true">
                          <use xlinkHref="#icon--folder-dark" />
                        </svg>
                      : <svg className="empty-state__icon" width="150" height="150" aria-hidden="true">
                          <use xlinkHref="#icon--folder" />
                        </svg>
                    }
                  </div>
                : <ul className="teasers">
                  {isLoggedInState(state) && state.appState.productConfigurations.map((productConfiguration, key) => {
                    if (productConfiguration.Submitted) {
                      return <li className="teaser" key={key}>
                        <article className="teaser__inner">
                          <div className="teaser__text">
                            <h3 className="teaser__title">
                              {state.appState.products.kind == "loaded" && !!state.appState.products.value.find(p => p.Id == productConfiguration.ProductId) ? state.appState.products.value.find(p => p.Id == productConfiguration.ProductId).Name : ""}
                            </h3>
                            <ul className="teaser__details">
                              {productConfiguration.CharacteristicValues.map((value, value_key) => {
                                return productConfiguration.CharacteristicArray.kind == "loaded" && !!productConfiguration.CharacteristicArray.value.find(ch => ch.Id == value.CharacteristicId) ?
                                  productConfiguration.CharacteristicArray.value.find(ch => ch.Id == value.CharacteristicId).InOverview ?
                                    <li key={value.CharacteristicId}>{this.selectedCharacteristic(value.CharacteristicId, key)}, </li> : "" : ""
                              })}
                            </ul>
                          </div>
                          <ul className="teaser__actions">
                            <li className="teaser__action" key="delete_key">
                              <button
                                type="button"
                                className="teaser__button button--icon"
                                onClick={(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
                                  document.documentElement.classList.add('alert--toggled');
                                  this.props.setState(s => {
                                    if (isLoggedInState(s)) {
                                      return ({
                                        ...s,
                                        appState: {
                                          ...s.appState,
                                          deleteProductConfigurationKey: key,
                                          isAlertTypeOpen: {
                                            ...s.appState.isAlertTypeOpen,
                                            deleteProduct: !s.appState.isAlertTypeOpen.deleteProduct
                                          }
                                        }
                                      })
                                    }
                                  })
                                }}
                              >
                                <span className="sr-text">{i18next.t('quotation_request:product_overview.clickables.delete_product')}</span>
                                <svg className="button__icon" width="18px" height="20px" aria-hidden="true">
                                  <use xlinkHref="#icon--trash" />
                                </svg>
                              </button>
                              <Overlay type="alert" alertType="deleteProduct" {...this.props} />
                            </li>
                            <li className="teaser__action" key="counter_key">
                              <Counter
                                handleDecrease={() => this.props.setState(s => {
                                  if (isLoggedInState(s)) {
                                    let prod_configs = s.appState.productConfigurations
                                    prod_configs.get(key).Count = prod_configs.get(key).Count - 1
                                    return ({ ...s, appState: { ...s.appState, productConfigurations: prod_configs } })
                                  }
                                })}
                                handleIncrease={() => this.props.setState(s => {
                                  if (isLoggedInState(s)) {
                                    let prod_configs = s.appState.productConfigurations
                                    prod_configs.get(key).Count = prod_configs.get(key).Count + 1
                                    return ({ ...s, appState: { ...s.appState, productConfigurations: prod_configs } })
                                  }
                                })}
                                isDisabled={this.props.state.appState.productConfigurations.get(key).Count < 2}
                                {...this.props}
                                label={isLoggedInState(state) && state.appState.productConfigurations.get(key) ? state.appState.productConfigurations.get(key).Count : 0}
                              />
                            </li>
                            <li className="teaser__action" key="edit_key">
                              <button
                                type="button"
                                className="teaser__button button--icon"
                                onClick={() => {
                                  document.documentElement.classList.add('panel--toggled');
                                  this.props.setState(s => ({ ...s, appState: { ...s.appState, showProducts: true, isPanelOpen: !this.props.state.appState.isPanelOpen, selectedProductConfiguration: key } }))
                                }}
                              >
                                <span className="sr-text">{i18next.t('quotation_request:product_overview.clickables.edit_product')}</span>
                                <svg className="button__icon" width="18px" height="20px" aria-hidden="true">
                                  <use xlinkHref="#icon--edit" />
                                </svg>
                              </button>
                            </li>
                          </ul>
                        </article>
                      </li>
                    }
                  }
                  ).toArray()}
                </ul>
              }
            </div>
          </section>
          
          <div className="section section--validation container container--m gutter--m">
            {this.notifications(state).run(() => s => s)}
          </div>

          <button type="submit" className="button button--block">
            <span className="button__label">{i18next.t(`quotation_request:project_information.fields.submit`)}</span>
            <svg className="button__icon" width="27px" height="21px" aria-hidden="true">
              <use xlinkHref="#icon--upload-cloud" />
            </svg>
          </button>
        </form>
        <Overlay type="alert" alertType="quotationSubmit" {...this.props} />
      </>
      :
      <>
        {asyncWithConnectionError<QuotationResponse>(
          response => mkWidget<Action<AsyncState<QuotationResponse>>>({
            run: setQuot => (
              QuotationStatusElement(() => isLoggedInState(state) ? state.user : null, setQuot, state)
            )
          })
        )(state.appState.quotation_response).run(
          updateQuotationResponse =>
            this.props.setState(s => {
              let { newResult, connectionErrorServer, connectionErrorUser } = updateQuotationResponse(s.appState.quotation_response)
              let toRetry: RequestToRetry = connectionErrorServer || connectionErrorUser ? { kind: "quotation_response", loading: {...s.appState.quotation_response, retryCount: 0} } : s.appState.toRetry
              return {
                ...s,
                appState: {
                  ...s.appState,
                  toRetry,
                  quotation_response: newResult,
                  isAlertTypeOpen: { ...s.appState.isAlertTypeOpen, connectionErrorUser, connectionErrorServer }
                }
              }
            }
            )
          )
        }
        {state.appState.quotation_response.kind == "failed" ?
          <section className="section section--request-submitted container container--m gutter--m">
            <div className="section__inner">
              <header className="section__header">
                <h1 className="section__title title--l">{i18next.t('quotation_request:submit.title_failed.a')}<span> {i18next.t('quotation_request:submit.title_failed.b')}</span></h1>
              </header>

              <p className="section__intro section__text">{i18next.t('quotation_request:submit.text_failed')}</p>
            </div>
          </section>
        : null}
      </>
      :
      <></>
  }
  selectedCharacteristic(id: string, prodconfig_key: string) {
    let state = this.props.state
    let productConfiguration = state.appState.productConfigurations.get(prodconfig_key)
    if (productConfiguration.CharacteristicValues.find(value => value.CharacteristicId == id) && productConfiguration.CharacteristicArray.kind == "loaded" &&
      productConfiguration.CharacteristicArray.value.find(value => value.Id == id)) {
      let characteristic = productConfiguration.CharacteristicArray.value.find(value => value.Id == id)
      let valueId = productConfiguration.CharacteristicValues.find(value => value.CharacteristicId == id)
      if (characteristic.ValueOptions.find(option => option.ValueId == valueId.CharacteristicValueId)) {
        return `${characteristic.Text}: ${characteristic.ValueOptions.find(option => option.ValueId == valueId.CharacteristicValueId).Value}`
      } else {
        return `${characteristic.Text}: -`
      }
    } else {
      return ""
    }
  }
}

export const QuotationRequestElement =
  (
    user: () => User<ExtraUserInfo>,
    setState: (_: Action<LoginState<State, ExtraUserInfo>>) => void,
    state: LoginState<State, ExtraUserInfo>
  ): JSX.Element => <QuotationRequest user={user} setState={setState} state={state} />
